import React, { useCallback, useState } from "react"
import { toast } from "react-toastify"
import {
  TagIcon,
  XMarkIcon,
  SparklesIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import {
  useAutoSuggestTagsMutation,
  type AutoTagSuggestionResponse,
} from "~/ui-rtk/api/tagAssignmentsApi"
import {
  useTagCategories,
  useItemStructuredTags,
} from "~/ui-rtk/hooks/use-structured-tags"
import type { TagCategory } from "~/ui-rtk/types/structured-tags"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { TagCategoryRow } from "./TagCategoryRow"
import { TagPillGroup, type TagPill } from "./TagPillGroup"

interface TagPanelProps {
  itemId: string
  itemType: "campaign" | "adset" | "ad" | "post"
  className?: string
  /** Pre-resolved pills from bulk-get (already has labels/colors, no lookup needed) */
  preloadedPills?: TagPill[]
  categories?: TagCategory[]
  adName?: string
  adSetName?: string
  campaignName?: string
  platform?: string
  /** True while the parent's bulk-get for tag assignments is still in flight. */
  isLoadingPills?: boolean
}

export const TagPanel: React.FC<TagPanelProps> = ({
  itemId,
  itemType,
  className,
  preloadedPills,
  categories: preloadedCategories,
  adName,
  adSetName,
  campaignName,
  platform,
  isLoadingPills = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  // Track options created during this session (AI suggest or manual)
  const [createdOptionsMap, setCreatedOptionsMap] = useState<
    Record<
      string,
      Array<{
        id: string
        value: string
        label: string
        color: string
        isSystem: boolean
        sortOrder: number
      }>
    >
  >({})

  const {
    categories: fetchedCategories,
    isLoading: categoriesLoading,
    createOption,
    bulkCreateOptions,
    createCategory,
    createCategories,
    renameCategory,
  } = useTagCategories()
  const [isAddingCategory, setIsAddingCategory] = useState(false)
  const [newCategoryLabel, setNewCategoryLabel] = useState("")
  const [newCategoryAllowMultiple, setNewCategoryAllowMultiple] = useState(true)
  const [newCategoryValueType, setNewCategoryValueType] = useState<
    "predefined" | "user_generated" | "mixed" | "free_text"
  >("user_generated")
  const [newCategoryOptions, setNewCategoryOptions] = useState<string[]>([])
  const [newCategoryOptionDraft, setNewCategoryOptionDraft] = useState("")
  const [isCreatingCategory, setIsCreatingCategory] = useState(false)

  const closeAddCategoryDialog = useCallback(() => {
    setIsAddingCategory(false)
    setNewCategoryLabel("")
    setNewCategoryAllowMultiple(true)
    setNewCategoryValueType("user_generated")
    setNewCategoryOptions([])
    setNewCategoryOptionDraft("")
  }, [])

  const addNewCategoryOption = useCallback(() => {
    const v = newCategoryOptionDraft.trim()
    if (!v) return
    setNewCategoryOptions(prev => (prev.includes(v) ? prev : [...prev, v]))
    setNewCategoryOptionDraft("")
  }, [newCategoryOptionDraft])
  const categories =
    preloadedCategories && preloadedCategories.length > 0
      ? preloadedCategories
      : fetchedCategories

  const {
    tagState: fetchedTagState,
    setCategoryValues,
    save,
    discard,
    isDirty,
    isSaving,
    isLoading: assignmentsLoading,
  } = useItemStructuredTags(itemType, itemId, { skip: !isOpen })

  const [autoSuggest, { isLoading: isSuggesting }] =
    useAutoSuggestTagsMutation()

  const isLoading = isOpen && (categoriesLoading || assignmentsLoading)

  const handleClose = () => {
    discard()
    setIsOpen(false)
  }

  // Saving keeps the modal open — the user closes it explicitly (X / Close).
  // Auto-closing on save threw away the context mid-flow when tagging several
  // categories in passes.
  const handleSave = async () => {
    try {
      await save()
      toast.success("Tags saved")
    } catch {
      toast.error("Failed to save tags")
    }
  }

  /**
   * Run AI suggestion and immediately apply results.
   * 1. Get suggestions from AI
   * 2. Bulk-create all new options in one request
   * 3. Select all suggested options
   */
  const handleAutoSuggest = useCallback(async () => {
    if (!adName) return

    setIsApplying(true)
    try {
      // Build existing tags from current state: { slug: [label, ...] }
      const existingTags: Record<string, string[]> = {}
      for (const cat of categories) {
        const selectedIds = fetchedTagState[cat.slug] ?? []
        if (selectedIds.length > 0) {
          const labels = selectedIds
            .map(id => cat.options.find(o => o.id === id)?.label)
            .filter(Boolean) as string[]
          if (labels.length > 0) {
            existingTags[cat.slug] = labels
          }
        }
      }

      const result: AutoTagSuggestionResponse = await autoSuggest({
        adName,
        adSetName,
        campaignName,
        platform,
        existingTags:
          Object.keys(existingTags).length > 0 ? existingTags : undefined,
      }).unwrap()

      // Step 1: collect all AI-proposed new categories and create them in ONE request.
      const categoryBySlug = new Map(categories.map(c => [c.slug, c]))
      const newCategoryInputs = Object.entries(result.suggestions)
        .filter(
          ([slug, s]) =>
            !categoryBySlug.has(slug) && s.isNewCategory && s.values.length > 0,
        )
        .map(([slug, s]) => {
          const valueType = s.valueType ?? "mixed"
          const seedOptions =
            valueType === "predefined" || valueType === "mixed"
              ? s.values.map(v => ({ label: v.label, value: v.value }))
              : []
          return {
            label: s.categoryLabel ?? slug,
            slug,
            valueType,
            allowMultiple: s.allowMultiple ?? false,
            options: seedOptions,
          }
        })

      if (newCategoryInputs.length > 0) {
        try {
          const { created, failed } = await createCategories(newCategoryInputs)
          for (const c of created) categoryBySlug.set(c.slug, c)
          for (const f of failed) {
            toast.error(`Could not create category "${f.slug}": ${f.reason}`)
          }
        } catch (err) {
          const message =
            err instanceof Error
              ? err.message
              : "Failed to create AI-proposed categories"
          toast.error(message)
        }
      }

      // Step 2: collect all new options that need creating in known categories.
      const toCreate: Record<
        string,
        Array<{ label: string; value: string }>
      > = {}

      for (const [slug, { values }] of Object.entries(result.suggestions)) {
        const cat = categoryBySlug.get(slug)
        if (!cat) continue

        for (const val of values) {
          const exists = cat.options.find(o => o.value === val.value)
          if (!exists) {
            if (!toCreate[slug]) toCreate[slug] = []
            toCreate[slug].push({ label: val.label, value: val.value })
          }
        }
      }

      // Bulk create all new options in one request
      let createdMap: Record<
        string,
        Array<{
          id: string
          value: string
          label: string
          color: string
          isSystem: boolean
          sortOrder: number
        }>
      > = {}
      if (Object.keys(toCreate).length > 0) {
        createdMap = await bulkCreateOptions(toCreate)
        // Track created options for display
        setCreatedOptionsMap(prev => {
          const next = { ...prev }
          for (const [slug, opts] of Object.entries(createdMap)) {
            next[slug] = [...(next[slug] ?? []), ...opts]
          }
          return next
        })
      }

      // Step 3: select all suggested options by ID.
      for (const [slug, { values }] of Object.entries(result.suggestions)) {
        const cat = categoryBySlug.get(slug)
        if (!cat) continue

        const optionIds: string[] = []
        const newOpts = createdMap[slug] ?? []

        for (const val of values) {
          // Check existing options (incl. options seeded during category creation)
          const existing = cat.options.find(o => o.value === val.value)
          if (existing) {
            optionIds.push(existing.id)
            continue
          }
          // Check newly created options
          const created = newOpts.find(o => o.value === val.value)
          if (created) {
            optionIds.push(created.id)
          }
        }

        if (optionIds.length > 0) {
          setCategoryValues(slug, optionIds)
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Auto-suggest failed"
      toast.error(`AI tag suggestion failed: ${message}`)
    } finally {
      setIsApplying(false)
    }
  }, [
    adName,
    adSetName,
    campaignName,
    platform,
    fetchedTagState,
    autoSuggest,
    categories,
    bulkCreateOptions,
    createCategories,
    setCategoryValues,
  ])

  const handleCreateCategory = useCallback(async () => {
    const label = newCategoryLabel.trim()
    if (!label) return

    const wantsOptions =
      newCategoryValueType === "predefined" || newCategoryValueType === "mixed"
    const pendingDraft = newCategoryOptionDraft.trim()
    const finalOptions = wantsOptions
      ? [
          ...newCategoryOptions,
          ...(pendingDraft && !newCategoryOptions.includes(pendingDraft)
            ? [pendingDraft]
            : []),
        ]
      : []

    if (newCategoryValueType === "predefined" && finalOptions.length === 0) {
      toast.error("Predefined categories need at least one option")
      return
    }

    setIsCreatingCategory(true)
    try {
      await createCategory({
        label,
        valueType: newCategoryValueType,
        allowMultiple: newCategoryAllowMultiple,
        options: finalOptions.map(o => ({ label: o })),
      })
      closeAddCategoryDialog()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create category"
      toast.error(message)
    } finally {
      setIsCreatingCategory(false)
    }
  }, [
    newCategoryLabel,
    newCategoryValueType,
    newCategoryAllowMultiple,
    newCategoryOptions,
    newCategoryOptionDraft,
    createCategory,
    closeAddCategoryDialog,
  ])

  const isBusy = isSuggesting || isApplying

  return (
    <div className={cn("w-full", className)} onClick={e => e.stopPropagation()}>
      <TagPillGroup
        pills={preloadedPills ?? []}
        onClick={() => setIsOpen(true)}
        isLoading={isLoadingPills}
      />

      {isOpen && (
        <Dialog
          isOpen={isOpen}
          handleClose={() => void handleClose()}
          width={700}
          height="auto"
          // A stray outside click must not silently discard edits or interrupt
          // a save — closing then requires the explicit X / Close button.
          closeOnClickOutside={!isDirty && !isSaving}
        >
          <div
            className="bg-system-blue-800 border border-system-blue-600/50 rounded-system-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-system-blue-700 border-b border-system-blue-600/50 px-6 py-4 rounded-t-system-xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-system-blue-500/20 rounded-lg flex items-center justify-center">
                    <TagIcon className="w-5 h-5 text-system-blue-300" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Tag {itemType.charAt(0).toUpperCase() + itemType.slice(1)}
                  </h2>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isSaving}
                  className="text-system-gray-400 hover:text-white transition-colors p-1 rounded-md hover:bg-system-blue-600/20 disabled:opacity-50"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div
              className="flex-1 overflow-y-auto px-6 py-4 min-h-0"
              style={{ scrollbarGutter: "stable" }}
            >
              {/* Ad / AdSet / Campaign info + AI Suggest */}
              {(adName || adSetName || campaignName) && (
                <div className="mb-4">
                  <div className="space-y-1.5 text-xs">
                    {adName && (
                      <div className="flex gap-2">
                        <span className="text-system-gray-500 flex-shrink-0 w-16 font-medium">
                          Ad
                        </span>
                        <span className="text-system-gray-300 break-all">
                          {adName}
                        </span>
                      </div>
                    )}
                    {adSetName && (
                      <div className="flex gap-2">
                        <span className="text-system-gray-500 flex-shrink-0 w-16 font-medium">
                          Ad Set
                        </span>
                        <span className="text-system-gray-300 break-all">
                          {adSetName}
                        </span>
                      </div>
                    )}
                    {campaignName && (
                      <div className="flex gap-2">
                        <span className="text-system-gray-500 flex-shrink-0 w-16 font-medium">
                          Campaign
                        </span>
                        <span className="text-system-gray-300 break-all">
                          {campaignName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <span className="loading loading-spinner loading-md text-system-blue-400" />
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-end mb-2">
                    <button
                      onClick={() => setIsAddingCategory(true)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-system-blue-400 hover:text-system-blue-300 border border-dashed border-system-blue-500/30 rounded-lg transition-colors cursor-pointer"
                    >
                      <PlusIcon className="w-3 h-3" />
                      Add category
                    </button>
                  </div>
                  {(() => {
                    const sorted = [...categories].sort((a, b) => {
                      // User-defined first, then system. Within each group,
                      // preserve the existing sortOrder.
                      if (a.isSystem !== b.isSystem) return a.isSystem ? 1 : -1
                      return a.sortOrder - b.sortOrder
                    })
                    const firstSystemIdx = sorted.findIndex(c => c.isSystem)
                    return sorted.map((cat, idx) => {
                      // Merge any options created during this session
                      const extra = createdOptionsMap[cat.slug] ?? []
                      const merged =
                        extra.length > 0
                          ? {
                              ...cat,
                              options: [
                                ...cat.options,
                                ...extra.filter(
                                  e => !cat.options.find(o => o.id === e.id),
                                ),
                              ],
                            }
                          : cat

                      return (
                        <React.Fragment key={cat.id}>
                          {idx === firstSystemIdx && (
                            <div className="mt-4 mb-1 pt-3 border-t border-system-blue-600/30">
                              <h5 className="text-[10px] font-semibold uppercase tracking-wider text-system-blue-300">
                                Default
                              </h5>
                            </div>
                          )}
                          <TagCategoryRow
                            category={merged}
                            selectedOptionIds={fetchedTagState[cat.slug] ?? []}
                            onChange={optionIds =>
                              setCategoryValues(cat.slug, optionIds)
                            }
                            onCreateOption={label =>
                              createOption(cat.slug, label)
                            }
                            onRename={label => renameCategory(cat.slug, label)}
                            // Free-text values typed here stay local until the
                            // modal's Save — discarding persists nothing.
                            deferFreeTextCreation
                          />
                        </React.Fragment>
                      )
                    })
                  })()}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-system-blue-600/50 bg-system-blue-600/5">
              <div className="flex justify-between items-center px-6 py-4">
                {adName && (
                  <button
                    onClick={() => void handleAutoSuggest()}
                    disabled={isBusy}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-system-brand-pink-500 text-white rounded-lg hover:bg-system-brand-pink-600 disabled:opacity-50 transition-all"
                  >
                    <SparklesIcon className="w-3.5 h-3.5" />
                    {isBusy ? "Analyzing..." : "AI Suggest"}
                  </button>
                )}
                {!adName && (
                  <div className="text-xs text-system-gray-500">
                    {isDirty ? "Unsaved changes" : "All changes saved"}
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm text-system-gray-400 hover:text-white transition-colors disabled:opacity-50"
                  >
                    {isDirty ? "Discard" : "Close"}
                  </button>
                  {isDirty && (
                    <button
                      onClick={() => void handleSave()}
                      disabled={isSaving}
                      className="px-6 py-2 text-sm rounded-lg font-medium transition-all duration-200 bg-system-brand-pink-500 text-white hover:bg-system-brand-pink-600"
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}

      {isAddingCategory && (
        <Dialog
          isOpen={isAddingCategory}
          handleClose={closeAddCategoryDialog}
          width={480}
          height="auto"
          closeOnClickOutside={false}
        >
          <div
            className="bg-system-blue-800 border border-system-blue-600/50 rounded-system-xl shadow-2xl w-full flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-system-blue-700 border-b border-system-blue-600/50 px-6 py-4 rounded-t-system-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-system-blue-500/20 rounded-lg flex items-center justify-center">
                    <PlusIcon className="w-5 h-5 text-system-blue-300" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    New Category
                  </h2>
                </div>
                <button
                  onClick={closeAddCategoryDialog}
                  className="text-system-gray-400 hover:text-white transition-colors p-1 rounded-md hover:bg-system-blue-600/20"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-system-gray-300 mb-1.5">
                  Name
                </label>
                <input
                  type="text"
                  value={newCategoryLabel}
                  onChange={e => setNewCategoryLabel(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") void handleCreateCategory()
                    if (e.key === "Escape") closeAddCategoryDialog()
                  }}
                  placeholder="e.g. Mood, Season, Audience"
                  className="w-full px-3 py-2 text-sm border border-system-blue-500/30 rounded bg-system-blue-600/10 text-white placeholder-system-gray-500 focus:outline-none focus:ring-1 focus:ring-system-blue-400/50"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-system-gray-300 mb-1.5">
                  Selection
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewCategoryAllowMultiple(false)}
                    className={cn(
                      "flex-1 px-3 py-2 text-xs rounded border transition-colors",
                      !newCategoryAllowMultiple
                        ? "bg-system-blue-500/20 border-system-blue-400 text-white"
                        : "bg-system-blue-600/10 border-system-blue-600/30 text-system-gray-400 hover:text-system-gray-200",
                    )}
                  >
                    Single value
                  </button>
                  <button
                    onClick={() => setNewCategoryAllowMultiple(true)}
                    className={cn(
                      "flex-1 px-3 py-2 text-xs rounded border transition-colors",
                      newCategoryAllowMultiple
                        ? "bg-system-blue-500/20 border-system-blue-400 text-white"
                        : "bg-system-blue-600/10 border-system-blue-600/30 text-system-gray-400 hover:text-system-gray-200",
                    )}
                  >
                    Multiple values
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-system-gray-300 mb-1.5">
                  Value type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(
                    [
                      {
                        key: "user_generated",
                        label: "Open list",
                        hint: "Users add values",
                      },
                      {
                        key: "predefined",
                        label: "Predefined",
                        hint: "Curated list",
                      },
                      { key: "mixed", label: "Mixed", hint: "Both" },
                      {
                        key: "free_text",
                        label: "Custom text",
                        hint: "Typed per item, no list",
                      },
                    ] as const
                  ).map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setNewCategoryValueType(opt.key)}
                      className={cn(
                        "px-2 py-2 text-xs rounded border transition-colors text-left",
                        newCategoryValueType === opt.key
                          ? "bg-system-blue-500/20 border-system-blue-400 text-white"
                          : "bg-system-blue-600/10 border-system-blue-600/30 text-system-gray-400 hover:text-system-gray-200",
                      )}
                    >
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-[10px] text-system-gray-500 mt-0.5">
                        {opt.hint}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {(newCategoryValueType === "predefined" ||
                newCategoryValueType === "mixed") && (
                <div>
                  <label className="block text-xs font-medium text-system-gray-300 mb-1.5">
                    Options
                    {newCategoryValueType === "mixed" && (
                      <span className="ml-1 text-[10px] text-system-gray-500">
                        (users can add more later)
                      </span>
                    )}
                  </label>
                  <div className="flex gap-1.5 mb-2">
                    <input
                      type="text"
                      value={newCategoryOptionDraft}
                      onChange={e => setNewCategoryOptionDraft(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          addNewCategoryOption()
                        }
                      }}
                      placeholder="Type an option and press Enter"
                      className="flex-1 px-3 py-2 text-sm border border-system-blue-500/30 rounded bg-system-blue-600/10 text-white placeholder-system-gray-500 focus:outline-none focus:ring-1 focus:ring-system-blue-400/50"
                    />
                    <button
                      onClick={addNewCategoryOption}
                      disabled={!newCategoryOptionDraft.trim()}
                      className="px-3 py-2 text-xs bg-system-blue-500/20 text-system-blue-200 border border-system-blue-500/40 rounded hover:bg-system-blue-500/30 disabled:opacity-50 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {newCategoryOptions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {newCategoryOptions.map(opt => (
                        <span
                          key={opt}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-system-blue-100 text-system-blue-800 border border-system-blue-200"
                        >
                          {opt}
                          <button
                            onClick={() =>
                              setNewCategoryOptions(prev =>
                                prev.filter(o => o !== opt),
                              )
                            }
                            className="hover:text-system-blue-900"
                            aria-label={`Remove ${opt}`}
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex-shrink-0 border-t border-system-blue-600/50 bg-system-blue-600/5 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={closeAddCategoryDialog}
                className="px-4 py-2 text-sm text-system-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleCreateCategory()}
                disabled={!newCategoryLabel.trim() || isCreatingCategory}
                className="px-6 py-2 text-sm rounded-lg font-medium transition-all duration-200 bg-system-brand-pink-500 text-white hover:bg-system-brand-pink-600 disabled:opacity-50"
              >
                {isCreatingCategory ? "Creating..." : "Create category"}
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default TagPanel
